import React, { useState, useEffect } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Row,
  Col,
  Label,
} from "reactstrap"
import { FaPlus, FaTrash } from "react-icons/fa6"
import Swal from "sweetalert2"
import activities from "utils/Activities"

const WorkoutModal = ({ isOpen, toggle, onSave, event }) => {
  const [step, setStep] = useState(2)
  const [activityName, setActivityName] = useState("")
  const [formData, setFormData] = useState({
    activityName: "",
    activityTitle: "",
    plannedDuration: "",
    plannedDistance: "",
    preActivityComments: "",
    postActivityComments: "",
    description: "",
    workoutMedia: null,
    buildWorkout: [], // New field for dynamic workout segments
    endTime: "",
    startTime: "",
  })
  const [workoutMediaLink, setWorkoutMediaLink] = useState("")

  // Populate form when editing
  useEffect(() => {
    console.log(event)
    if (isOpen) {
      if (!event?.workout) {
        return
      }
      setStep(2)
      setActivityName(event?.workout?.activityName || "")
      setFormData({
        activityName: event?.workout?.activityName || "",
        activityTitle: event?.workout.activityTitle || "",
        plannedDuration: event?.workout.plannedDuration || "",
        plannedDistance: event?.workout.plannedDistance || "",
        preActivityComments: event?.workout?.preActivityComments || "",
        postActivityComments: event?.workout?.postActivityComments || "",
        description: event?.workout.description || "",
        workoutMedia: null,
        buildWorkout: event?.workout.buildWorkout || [],
        endTime: event?.workout.endTime,
        startTime: event?.workout.startTime,
      })
      setWorkoutMediaLink(event?.workout?.workoutMedia)
    }
  }, [isOpen, event])

  const handleActivitySelect = name => {
    setActivityName(name)
    setFormData(prev => ({ ...prev, activityTitle: name, activityName: name }))
  }

  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  // Add a new segment to buildWorkout
  const handleAddSegment = () => {
    setFormData(prev => ({
      ...prev,
      buildWorkout: [
        ...prev.buildWorkout,
        { typeOption: "warmup", duration: "", zone: "", rpe: "" },
      ],
    }))
  }

  // Update a segment
  const handleSegmentChange = (index, field, value) => {
    const updated = [...formData.buildWorkout]
    updated[index][field] = value
    setFormData(prev => ({ ...prev, buildWorkout: updated }))
  }

  // Delete a segment
  const handleDeleteSegment = index => {
    const updated = [...formData.buildWorkout]
    updated.splice(index, 1)
    setFormData(prev => ({ ...prev, buildWorkout: updated }))
  }

  // Calculate total planned duration (sum of segment durations in HH:MM:SS)
  const getTotalPlannedDuration = () => {
    let totalSeconds = 0
    formData.buildWorkout.forEach(seg => {
      if (seg.duration) {
        const parts = seg.duration.split(":").map(p => parseInt(p, 10))
        if (parts.length === 3)
          totalSeconds += parts[0] * 3600 + parts[1] * 60 + parts[2]
        else if (parts.length === 2) totalSeconds += parts[0] * 60 + parts[1]
        else totalSeconds += parts[0]
      }
    })
    const h = Math.floor(totalSeconds / 3600)
      .toString()
      .padStart(2, "0")
    const m = Math.floor((totalSeconds % 3600) / 60)
      .toString()
      .padStart(2, "0")
    const s = (totalSeconds % 60).toString().padStart(2, "0")
    return `${h}:${m}:${s}`
  }

  const handleSave = () => {
    // Common: must have activityName
    if (!formData.activityName) {
      Swal.fire({ title: "Please select an activity", icon: "warning" })
      return
    }

    const name = formData.activityName

    // 🟢 Validation per activity type
    if (name === "Note") {
      if (!formData.activityTitle) {
        Swal.fire({ title: "Title is required", icon: "warning" })
        return
      }
      if (!formData.description) {
        Swal.fire({ title: "Note content is required", icon: "warning" })
        return
      }
    } else if (name === "Attachment") {
      if (!formData.activityTitle) {
        Swal.fire({ title: "Title is required", icon: "warning" })
        return
      }
      if (!formData.workoutMedia) {
        Swal.fire({ title: "Please upload a file", icon: "warning" })
        return
      }
    } else if (name === "Event") {
      if (!formData.activityTitle) {
        Swal.fire({ title: "Event title is required", icon: "warning" })
        return
      }
      if (!formData.startTime || !formData.endTime) {
        Swal.fire({ title: "Start and end time are required", icon: "warning" })
        return
      }
    } else if (name === "Day off") {
      if (!formData.activityTitle) {
        Swal.fire({ title: "Title is required", icon: "warning" })
        return
      }
    } else {
      // Default: workout-type activities
      if (!formData.activityTitle) {
        Swal.fire({ title: "Title is required", icon: "warning" })
        return
      }
      if (!formData.description) {
        Swal.fire({ title: "Description is required", icon: "warning" })
        return
      }
      if (formData.buildWorkout.length === 0) {
        Swal.fire({
          title: "Add at least one workout segment",
          icon: "warning",
        })
        return
      }
    }

    // ✅ If all validations pass → compute total duration (for workout types)
    let totalDuration = ""
    if (
      name !== "Note" &&
      name !== "Attachment" &&
      name !== "Event" &&
      name !== "Day off"
    ) {
      totalDuration = getTotalPlannedDuration()
    }

    const dataToSave = {
      ...formData,
      plannedDuration: totalDuration,
    }

    // Save and reset
    onSave(dataToSave, event?.workout?._id)
    setStep(1)
    setFormData({
      activityName: "",
      activityTitle: "",
      plannedDuration: "",
      plannedDistance: "",
      preActivityComments: "",
      postActivityComments: "",
      description: "",
      workoutMedia: null,
      buildWorkout: [],
      endTime: "",
      startTime: "",
    })
    toggle()
  }

  return (
    <Modal isOpen={isOpen} toggl e={toggle} size="xl" centered fullscreen="xl">
      <ModalHeader toggle={toggle}>
        {event ? "Edit Workout" : "Workout Details"}
      </ModalHeader>
      <ModalBody>
        {step === 1 && (
          <div>
            <Label className="mb-2 fw-bold">
              Select Activity <span className="text-danger">*</span>
            </Label>
            <Row className="g-3">
              {activities.map(({ name, icon }) => (
                <Col key={name} xs={2} sm={2} md={2}>
                  <Button
                    color={activityName === name ? "primary" : "light"}
                    onClick={() => handleActivitySelect(name)}
                    className="w-100 d-flex flex-column align-items-center p-3 shadow-sm"
                    style={{
                      border:
                        activityName === name
                          ? "2px solid #0d6efd"
                          : "1px solid #dee2e6",
                      borderRadius: "10px",
                      transition: "transform 0.2s",
                    }}
                    onMouseEnter={e =>
                      (e.currentTarget.style.transform = "scale(1.05)")
                    }
                    onMouseLeave={e =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                  >
                    <div style={{ fontSize: "2rem" }}>{icon}</div>
                    <small className="mt-1">{name}</small>
                  </Button>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {step === 2 && (
          <>
            {activityName === "Note" && (
              <Row className="mt-3">
                <div className="mb-3">
                  <Label className="fw-bold">
                    Title <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="activityTitle"
                    value={formData.activityTitle}
                    onChange={handleInputChange}
                    placeholder="Activity title"
                  />
                </div>
                <Col md={12}>
                  <Label>Note</Label>
                  <Input
                    type="textarea"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Write your note here..."
                  />
                </Col>
              </Row>
            )}

            {activityName === "Day off" && (
              <Row className="mt-3">
                <div className="mb-3">
                  <Label className="fw-bold">
                    Title <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="activityTitle"
                    value={formData.activityTitle}
                    onChange={handleInputChange}
                    placeholder="Activity title"
                  />
                </div>
                <Col md={12}>
                  <Label>Description </Label>
                  <Input
                    type="textarea"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Write your note here..."
                  />
                </Col>
              </Row>
            )}

            {/* 📎 Attachment fields */}
            {activityName === "Attachment" && (
              <Row className="mt-3">
                <div className="mb-3">
                  <Label className="fw-bold">
                    Title <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="activityTitle"
                    value={formData.activityTitle}
                    onChange={handleInputChange}
                    placeholder="Activity title"
                  />
                </div>
                <Col md={12}>
                  <Label>Upload File</Label>
                  <Input
                    type="file"
                    name="attachment"
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        workoutMedia: e.target.files[0],
                      }))
                    }
                  />
                </Col>
              </Row>
            )}

            {/* 📅 Event fields */}
            {activityName === "Event" && (
              <Row className="mt-3">
                <div className="mb-3">
                  <Label className="fw-bold">
                    Title <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="activityTitle"
                    value={formData.activityTitle}
                    onChange={handleInputChange}
                    placeholder="Activity title"
                  />
                </div>
                <Col md={6}>
                  <Label>
                    Start Time <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="time"
                    name="startTime"
                    value={formData.startTime || ""}
                    onChange={handleInputChange}
                  />
                </Col>
                <Col md={6}>
                  <Label>
                    End Time <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="time"
                    name="endTime"
                    value={formData.endTime || ""}
                    onChange={handleInputChange}
                  />
                </Col>
              </Row>
            )}

            {activityName !== "Note" &&
              activityName !== "Attachment" &&
              activityName !== "Event" &&
              activityName !== "Day off" && (
                <Row>
                  <Col md={5}>
                    <div className="mb-3">
                      <Label className="fw-bold">
                        Title <span className="text-danger">*</span>
                      </Label>
                      <Input
                        name="activityTitle"
                        value={formData.activityTitle}
                        onChange={handleInputChange}
                        placeholder="Activity title"
                      />
                    </div>

                    <Row className="mb-3">
                      <Col>
                        <Label>Planned Distance (mi)</Label>
                        <Input
                          name="plannedDistance"
                          value={formData.plannedDistance}
                          onKeyPress={e => {
                            const { key, target } = e
                            const currentValue = target.value

                            // Allow digits
                            if (/[0-9]/.test(key)) return

                            // Allow only one decimal point
                            if (key === "." && !currentValue.includes("."))
                              return

                            // Block everything else
                            e.preventDefault()
                          }}
                          onChange={handleInputChange}
                        />
                      </Col>
                      <Col>
                        <Label>Total Planned Duration</Label>
                        <Input value={getTotalPlannedDuration()} disabled />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <Label>Completed Distance (mi)</Label>
                        <Input value={event?.completedDistance} disabled />
                      </Col>
                      <Col md={6}>
                        <Label>Completed Duration</Label>
                        <Input value={event?.completedDuration} disabled />
                      </Col>
                      <Col className="mt-3">
                        <Label>User Comment</Label>
                        <Input
                          type="textarea"
                          value={event?.usercomment}
                          rows={2}
                          disabled
                        />
                      </Col>
                    </Row>

                    <div className="mb-3">
                      <Label>Pre-activity comments:</Label>
                      <Input
                        type="textarea"
                        name="preActivityComments"
                        value={formData.preActivityComments}
                        onChange={handleInputChange}
                        rows={2}
                      />
                    </div>

                    <div className="mb-3">
                      <Label>Post-activity comments:</Label>
                      <Input
                        type="textarea"
                        name="postActivityComments"
                        value={formData.postActivityComments}
                        onChange={handleInputChange}
                        rows={2}
                      />
                    </div>

                    <div className="mb-3">
                      <Label>Description:</Label>
                      <Input
                        type="textarea"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={3}
                      />
                    </div>

                    <div className="mb-3">
                      <Label>Workout File:</Label>
                      <Input
                        type="file"
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            workoutMedia: e.target.files[0],
                          }))
                        }
                      />
                      {workoutMediaLink && (
                        <p className="mt-1">
                          <b>Uploaded File:</b>{" "}
                          {workoutMediaLink?.split("/")?.[1]}
                        </p>
                      )}
                    </div>
                  </Col>
                  <Col md={7}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="fw-bold">Build Workout</h5>
                      <Button color="primary" onClick={handleAddSegment}>
                        <FaPlus /> Add Segment
                      </Button>
                    </div>

                    <div
                      style={{
                        maxHeight: "500px",
                        overflowY: "auto",
                        overflowX: "hidden",
                        scrollbarWidth: "thin",
                      }}
                    >
                      {formData.buildWorkout.map((seg, idx) => (
                        <div
                          key={idx}
                          className="p-3 mb-3 shadow-sm rounded bg-light"
                          style={{ borderLeft: "4px solid #0d6efd" }}
                        >
                          <Row className="align-items-end">
                            <Col md={3} className="pe-1 ps-2">
                              <Label>Type</Label>
                              <Input
                                type="select"
                                value={seg.typeOption}
                                onChange={e =>
                                  handleSegmentChange(
                                    idx,
                                    "typeOption",
                                    e.target.value
                                  )
                                }
                              >
                                <option value="warmup">Warmup</option>
                                <option value="activity">Activity</option>
                                <option value="recovery">Recovery</option>
                                <option value="cooldown">Cooldown</option>
                              </Input>
                            </Col>

                            <Col md={4} className="px-1">
                              <Label>Duration (HH:MM:SS)</Label>
                              <Input
                                value={seg.duration}
                                placeholder="00:30:00"
                                onChange={e =>
                                  handleSegmentChange(
                                    idx,
                                    "duration",
                                    e.target.value
                                  )
                                }
                              />
                            </Col>

                            <Col md={4} className="px-1">
                              <Label>Zone</Label>
                              <Input
                                type="select"
                                value={seg.zone}
                                onChange={e =>
                                  handleSegmentChange(
                                    idx,
                                    "zone",
                                    e.target.value
                                  )
                                }
                              >
                                <option value="">Select Zone</option>
                                <option value="Zone 1">
                                  Zone 1 / Easy Walk
                                </option>
                                <option value="Zone 2">Zone 2 / Brisk</option>
                                <option value="Zone 3">Zone 3 / Steady</option>
                                <option value="Zone 4">
                                  Zone 4 / Moderate
                                </option>
                                <option value="Zone 5">Zone 5 / Hard</option>
                              </Input>
                            </Col>

                            <Col md={1} className="px-1">
                              <Button
                                color="danger"
                                onClick={() => handleDeleteSegment(idx)}
                              >
                                <FaTrash />
                              </Button>
                            </Col>
                          </Row>
                        </div>
                      ))}
                    </div>
                  </Col>
                </Row>
              )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={handleSave}>
          Update
        </Button>
        <Button color="danger" onClick={toggle}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default WorkoutModal
